import { Dropdown, Image, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Utilities } from '../../../MedicareProAPI';
import type { AuthContextType } from '../../Contexts/AuthProvider';
import {
  faAdjust,
  faCog,
  faCompass,
  faFileImport,
  faLifeRing,
  faLock,
  faNetworkWired,
  faPowerOff,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { NavLink, useNavigate } from 'react-router-dom';

export enum NavProfileTheme {
  Light = 'light',
  Dark = 'dark',
}

type NavProfileSize = 'sm' | 'md' | 'lg';

type NavProfileProps = {
  session: AuthContextType;
  theme?: NavProfileTheme;
  size?: NavProfileSize;
};

export const NAV_PROFILE_SIZE: Record<NavProfileSize, number> = {
  sm: 32,
  md: 50,
  lg: 64,
};

export const NAV_PROFILE_BORDER: Record<NavProfileSize, number> = {
  sm: 1,
  md: 2,
  lg: 2,
};

export const NavProfile = ({
  session,
  theme = NavProfileTheme.Dark,
  size = 'sm',
}: NavProfileProps) => {
  const AVATAR_SIZE = NAV_PROFILE_SIZE[size];
  const BORDER_SIZE = NAV_PROFILE_BORDER[size];

  const avatarInitialsClassList = [
    'me-1',
    'rounded-circle',
    'bg-primary',
    'text-white',
    'd-inline-flex',
    'justify-content-center',
    'align-items-center',
    'fw-bold',
    'text-uppercase',
    'border',
    'border-' + BORDER_SIZE,
    theme === NavProfileTheme.Dark ? 'border-light' : 'border-dark',
  ];

  if (size === 'sm') {
    avatarInitialsClassList.push('small');
  }

  const navigate = useNavigate();

  const handleSignOut = async () => {
    await session.logout();
    navigate('/');
  };

  return (
    <Nav className="ms-auto align-items-center">
      <Dropdown as={Nav.Item} align="end">
        <Dropdown.Toggle
          as={Nav.Link}
          id="user-menu"
          className={`p-0 ${theme === NavProfileTheme.Dark ? 'link-light' : 'link-dark'}`}
        >
          <span className="d-inline-flex align-items-center">
            {session.getUser().getProfilePic() ? (
              <Image
                src={session.getUser().getProfilePic()!.getProfilePicUrl()}
                alt="My avatar"
                roundedCircle
                width={AVATAR_SIZE}
                height={AVATAR_SIZE}
                className="me-1"
              />
            ) : (
              <span
                className={avatarInitialsClassList.join(' ')}
                style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                aria-label="My initials"
              >
                {Utilities.getNameInitials(
                  session.getUser().getUserFirstname(),
                  session.getUser().getUserLastname()
                )}
              </span>
            )}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="bg-white">
          <Dropdown.Item as={NavLink} to="/agent/settings" className="link-dark">
            <FontAwesomeIcon icon={faCog} /> Settings
          </Dropdown.Item>
          <Dropdown.Item as={NavLink} to="/agent/automations" className="link-dark">
            <FontAwesomeIcon icon={faNetworkWired} /> Automations
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item as={NavLink} to="/agent/settings/tutorials" className="link-dark">
            <FontAwesomeIcon className="text-success" icon={faVideo} /> Tutorials
          </Dropdown.Item>
          <Dropdown.Item
            as={NavLink}
            to="https://medicareprocrm.frontkb.com/en"
            className="link-dark"
          >
            <FontAwesomeIcon className="text-primary" icon={faLifeRing} /> Help
          </Dropdown.Item>
          <Dropdown.Item as={NavLink} to="/agent/import" className="link-dark">
            <FontAwesomeIcon icon={faFileImport} /> Import
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item>
            <FontAwesomeIcon icon={faAdjust} /> Dark Mode
          </Dropdown.Item>
          <Dropdown.Item>
            <FontAwesomeIcon icon={faCompass} /> Side Nav
          </Dropdown.Item>
          <Dropdown.Item>
            <FontAwesomeIcon icon={faLock} /> Lock Screen
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={handleSignOut}>
            <FontAwesomeIcon className="text-danger" icon={faPowerOff} /> Sign out
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </Nav>
  );
};
